import {ComparisonType} from "../conditionalRules/comparisonType";
import {ObjectModelPropertyRootType} from "../../enums/objectModelPropertyType";
import {TargetType} from "../conditionalRules/targetType";
import ObjectModelProperty from "../objectModelProperties/objectModelProperty";
import {ObjectHelper} from "../../helpers/objectHelper";
import {PickListSelectionType} from "~/enums/pickListSelectionType";
import {formulaFunctionHelper} from "~/helpers/formulaFunctionHelper";

export class ObjectModelPropertyConditionalHelpers {
  private static isText(property: ObjectModelProperty): boolean {
    return property.is(
      ObjectModelPropertyRootType.Text,
      ObjectModelPropertyRootType.LongText,
    );
  }

  private static isSingleChoice(property: ObjectModelProperty): boolean {
    return property.is(ObjectModelPropertyRootType.PickList) && property.selection_type === PickListSelectionType.SingleSelect;
  }

  private static isMultiChoice(property: ObjectModelProperty): boolean {
    return property.is(ObjectModelPropertyRootType.PickList) && property.selection_type === PickListSelectionType.MultiSelect;
  }

  private static isNumeric(property: ObjectModelProperty): boolean {
    return property.is(ObjectModelPropertyRootType.Number) || property.isNumeric;
  }

  private static isDateTime(property: ObjectModelProperty): boolean {
    return property.is(ObjectModelPropertyRootType.DateTime);
  }

  static effectiveTargetTypes(property: ObjectModelProperty) {
    const effective = this.effectiveProperty(property);
    const types = [TargetType.value];
    if (this.isMultiChoice(effective)) {
      types.push(TargetType.count);
    }
    return types;
  }

  static effectiveComparisonType(property: ObjectModelProperty, targetType: TargetType = TargetType.value): ComparisonType | null {
    const effective = this.effectiveProperty(property);
    if (this.isText(effective)) {
      return ComparisonType.text;
    } else if (this.isSingleChoice(effective)) {
      return ComparisonType.singleChoice;
    } else if (this.isMultiChoice(effective)) {
      if (targetType === TargetType.count) {
        return ComparisonType.numeric;
      } else {
        return ComparisonType.multiChoice;
      }
    } else if (this.isNumeric(effective)) {
      return ComparisonType.numeric;
    } else if (this.isDateTime(effective)) {
      return ComparisonType.datetime;
    } else if (effective.is(ObjectModelPropertyRootType.DerivedField)) {
      // handles derived fields whose formulas do not reference another property type
      // falls back to text
      return formulaFunctionHelper.effectiveComparisonType(effective.derived_value_formula) || ComparisonType.text;
    // re-add if needed; removed as relationship counts are numeric for Records
    // } else if (this.isInstanceCount(effective)) {
    //   return ComparisonType.instanceCount;
    }
  }

  static effectiveChoiceComparisonOptions(property: ObjectModelProperty): any[] {
    // the only choice comparison options are for pick lists, which store their options as properties inside themselves
    const effectiveProp = this.effectiveProperty(property);
    return ObjectHelper.toArray(effectiveProp.object_model_properties);
  }

  static effectiveProperty(property: ObjectModelProperty): ObjectModelProperty {
    if (property.isValidForConditionalLogic && property.is(ObjectModelPropertyRootType.MappedProperty)) {
      // currently we assume that all linked relation mapped properties refer to properties of the same type (this
      // will be enforced by the mapped property in the future), so grab the first related property and use its type
      const linkedRelProp = property.linked_relation_mapped_properties[0];
      // we need to recurse here, because mapped properties can be linked to mapped properties on other object models
      return this.effectiveProperty(linkedRelProp.linkedProperty);
    } else if (property.isValidForConditionalLogic && property.is(ObjectModelPropertyRootType.DerivedField)) {

      // derived fields use formulas that can reference any property type, including other derived fields
      // I'm currently assuming any referenced properties will match the formula eval type, but this may not be the case for logical formula funcs.
      const clientIds = property.derived_value_formula?.match(/f_([a-z0-9]{32})/);
      const formulaFunctionType =  formulaFunctionHelper.findFunctionData(property.derived_value_formula)?.type;
      if (clientIds && property.objectModel && (formulaFunctionType !== "logical")) {
        const referencedClientId = clientIds[1].replace(/([a-z0-9]{8})([a-z0-9]{4})([a-z0-9]{4})([a-z0-9]{4})([a-z0-9]{12})/, '$1-$2-$3-$4-$5');
        const referencedProp = property.objectModel.findObjectModelProperty(referencedClientId);
        // we need to recurse here, because derived fields may reference other derived fields on same object model
        return this.effectiveProperty(referencedProp);
      } else { // formula contains no client_ids
        return property;
      }
    } else {
      return property;
    }
  }
}